import React from 'react';

import { usePeriods } from 'components/hooks/usePeriods';

import {
  TimeLineItem,
  TimeLine,
  Grid,
  TextField,
  DatePicker,
  Alert,
  Stack,
} from '@gogame/components';
import { withFormField } from '@gogame/form';
import { dateFNS, toDate } from '@gogame/intl-ts';

import {
  ADDITIONAL_FEE_PERIOD_FROM,
  ADDITIONAL_FEE_PERIOD,
  ADDITIONAL_FEE_PERIOD_TO,
  TAX_FEE_PERCENT,
} from '../constants';

const createPeriod = ({ from, to }) => {
  return {
    [ADDITIONAL_FEE_PERIOD]: {
      [ADDITIONAL_FEE_PERIOD_FROM]: from,
      [ADDITIONAL_FEE_PERIOD_TO]: to,
    },
  };
};

const updatePeriod = (state, { from, to }) => {
  return {
    ...state,
    [ADDITIONAL_FEE_PERIOD]: {
      [ADDITIONAL_FEE_PERIOD_FROM]: from,
      [ADDITIONAL_FEE_PERIOD_TO]: to,
    },
  };
};

const EMPTY = [];

const AdditionalFeePeriods = withFormField({ padding: 0 })(({
  value = EMPTY,
  onChange,
  error,
}) => {
  const { add, remove, setFieldValue, setPeriodFieldValue } = usePeriods({
    value,
    onChange,
    createPeriod,
    updatePeriod,
  });
  const lastPeriodValid =
    !value.length ||
    value.slice(-1).pop()?.[ADDITIONAL_FEE_PERIOD]?.[
      ADDITIONAL_FEE_PERIOD_TO
    ] !== undefined;

  return (
    <Stack>
      <TimeLine
        fullWidth={true}
        withRemove={true}
        withAdd={true}
        addDisabled={!lastPeriodValid}
        addLabel={'Fee period'}
        onAdd={add}
      >
        {value?.map((periodFee, i) => {
          const prev = value[i - 1];
          const next = value[i + 1];
          const periodError = error?.[i];
          return (
            <TimeLineItem
              removable={true}
              onRemove={() => remove(i)}
              color={'info'}
            >
              <Grid container spacing={3}>
                <Grid item xs={2}>
                  <TextField
                    name={TAX_FEE_PERCENT}
                    type={'percent'}
                    padding={0}
                    label={'Fee'}
                    required={true}
                    value={periodFee[TAX_FEE_PERCENT]}
                    onChange={(_, v) => setFieldValue(i, TAX_FEE_PERCENT, v)}
                    error={periodError?.[TAX_FEE_PERCENT]}
                    fullWidth={true}
                  />
                </Grid>
                <Grid item xs={5}>
                  <DatePicker
                    padding={0}
                    label={'Start date'}
                    required={true}
                    fullWidth={true}
                    name={ADDITIONAL_FEE_PERIOD_FROM}
                    minDate={
                      prev
                        ? dateFNS.addDays(
                            toDate(
                              prev[ADDITIONAL_FEE_PERIOD][
                                ADDITIONAL_FEE_PERIOD_TO
                              ]
                            ),
                            1
                          )
                        : undefined
                    }
                    maxDate={
                      next
                        ? dateFNS.subDays(
                            toDate(
                              next[ADDITIONAL_FEE_PERIOD][
                                ADDITIONAL_FEE_PERIOD_TO
                              ]
                            ),
                            1
                          )
                        : undefined
                    }
                    onChange={(_, v) =>
                      setPeriodFieldValue(i, ADDITIONAL_FEE_PERIOD_FROM, v)
                    }
                    value={
                      periodFee?.[ADDITIONAL_FEE_PERIOD]?.[
                        ADDITIONAL_FEE_PERIOD_FROM
                      ]
                    }
                    error={
                      periodError?.[ADDITIONAL_FEE_PERIOD]?.[
                        ADDITIONAL_FEE_PERIOD_FROM
                      ]
                    }
                  />
                </Grid>
                <Grid item xs={5}>
                  <DatePicker
                    padding={0}
                    label={'End date'}
                    required={true}
                    fullWidth={true}
                    name={ADDITIONAL_FEE_PERIOD_TO}
                    onChange={(_, v) =>
                      setPeriodFieldValue(i, ADDITIONAL_FEE_PERIOD_TO, v)
                    }
                    value={
                      periodFee?.[ADDITIONAL_FEE_PERIOD]?.[
                        ADDITIONAL_FEE_PERIOD_TO
                      ]
                    }
                    error={
                      periodError?.[ADDITIONAL_FEE_PERIOD]?.[
                        ADDITIONAL_FEE_PERIOD_TO
                      ]
                    }
                    minDate={dateFNS.endOfDay(
                      toDate(
                        periodFee?.[ADDITIONAL_FEE_PERIOD]?.[
                          ADDITIONAL_FEE_PERIOD_FROM
                        ]
                      )
                    )}
                    maxDate={
                      next
                        ? dateFNS.subDays(
                            toDate(
                              next[ADDITIONAL_FEE_PERIOD][
                                ADDITIONAL_FEE_PERIOD_FROM
                              ]
                            ),
                            1
                          )
                        : undefined
                    }
                  />
                </Grid>
              </Grid>
            </TimeLineItem>
          );
        })}
      </TimeLine>
      {!lastPeriodValid ? (
        <Alert severity={'info'}>
          To add a new period, first set the end date of the last period.
        </Alert>
      ) : null}
    </Stack>
  );
});

export default AdditionalFeePeriods;
